import { memo, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import { CheckoutConfirmationResponseType } from 'src/models/Checkout/Confirmation';
import { useTHelper } from 'src/utils/i18n';
import { UiCard } from 'src/components/UI/Card';
import { Enums } from 'src/models/enums';
import { UiButton } from 'src/components/UI/Button';
import { UiInnerNotSPALink } from 'src/components/UI/InnerNotSPALink';
import { currentUserSelector } from 'src/store/user/selectors';
import { hardRedirect } from 'src/utils/hardRedirect';
import { deCommonCurrentPartnerSelector } from 'src/store/commonPart/selector';
import { signInOutSlice } from 'src/pages/Account/Profile/Signin/store/slice';
import { addReturnUrl } from 'src/utils/url';

type Props = {
  model: CheckoutConfirmationResponseType['ViralSharingSubsection'];
};

export const CheckoutConfirmationViralSharing = memo<Props>(({ model }) => {
  const tHelper = useTHelper('checkout.confirmation.viralSharing');

  const { Percentage } = Enums.campaigns.PromotionBonusTypeCode;

  const blockClassName = classNames('promoter-icon-container col-sm-4 col-xs-12', {
    'col-sm-9': model.AvailableBonusesCount === 1,
    'col-sm-5': model.AvailableBonusesCount === 2,
  });

  const { response } = useSelector(currentUserSelector);
  const { IsSsoEnabled } = useSelector(deCommonCurrentPartnerSelector);

  const doRedirectRef = useRef(false);

  const dispatch = useDispatch();

  const signUpAsPromoter = useCallback(() => {
    if (response?.IsAuthenticated) {
      hardRedirect(model.AddToPromotersUrl);
      return;
    }

    if (IsSsoEnabled) {
      hardRedirect(addReturnUrl('/account/logindispatcher', `${model.AddToPromotersUrl}&userType=Promoter`));
    } else {
      dispatch(signInOutSlice.actions.changeModal(true));
      doRedirectRef.current = true;
    }
  }, [IsSsoEnabled, dispatch, model.AddToPromotersUrl, response?.IsAuthenticated]);

  useEffect(() => {
    if (doRedirectRef.current && response?.IsAuthenticated) {
      hardRedirect(model.AddToPromotersUrl);
    }
  }, [model.AddToPromotersUrl, response?.IsAuthenticated]);

  return (
    <div className="row viral-sharing">
      <div className="col-sm-12">
        <h2 className="section-header">{model.FreeEntriesHeadline}</h2>
        <UiCard textCenter className="promoter-signup-container">
          <div className="row promoter-icons-container">
            {model.BonusQuantityForSocialMedia !== null && (
              <div className={blockClassName}>
                <div className="promoter-icon">
                  <img src="/Content/images/promoter-card/red-link.png" alt="" />
                </div>
                <div className="promoter-icon-title">
                  {tHelper('media', {
                    media: model.BonusQuantityForSocialMediaString || '',
                    terminology:
                      (model.BonusQuantityForSocialMedia === '1'
                        ? model.Terminology.Singular
                        : model.Terminology.Plural
                      )?.toLowerCase() || '',
                  })}
                </div>
              </div>
            )}

            {model.BonusAmountForSharer != null && (
              <div className={blockClassName}>
                <div className="promoter-icon">
                  <img src="/Content/images/promoter-card/yellow-link.png" alt="" />
                </div>
                <div className="promoter-icon-title">
                  {tHelper('sharer', {
                    sharer: model.BonusAmountForSharerString,
                    in: model.BonusType === Percentage ? 'in ' : '',
                    terminology:
                      (model.BonusType === Percentage || model.BonusAmountForSharer > 1
                        ? model.Terminology.Plural
                        : model.Terminology.Singular
                      )?.toLowerCase() || '',
                  })}
                </div>
              </div>
            )}
            {model.BonusAmountForPurchaser != null && (
              <div className={blockClassName}>
                <div className="promoter-icon">
                  <img src="/Content/images/promoter-card/ltblue-link.png" alt="" />
                </div>
                <div className="promoter-icon-title">
                  {tHelper('purchaser', {
                    purchaser: model.BonusAmountForPurchaserString,
                    more: model.BonusType === Percentage ? 'more ' : '',
                    terminology:
                      (model.BonusType === Percentage || model.BonusAmountForPurchaser > 1
                        ? model.Terminology.Plural
                        : model.Terminology.Singular
                      )?.toLowerCase() || '',
                  })}
                </div>
              </div>
            )}
          </div>
          <div className="row">
            <div className="col-sm-12 button-container promoter-signup-button-container">
              {model.LoggedAsPromoter ? (
                <UiInnerNotSPALink
                  className="btn BtnPrimary"
                  href={model.PromoterUrl || ''}
                  disabled={model.DisablePromoterButton}
                >
                  <span>{tHelper('view')}</span>
                </UiInnerNotSPALink>
              ) : (
                <UiButton
                  primary
                  tag="a"
                  className="btn btn-signup"
                  disabled={model.DisablePromoterButton}
                  onClick={signUpAsPromoter}
                >
                  {tHelper('signUp')}
                </UiButton>
              )}
            </div>
          </div>
        </UiCard>
      </div>
    </div>
  );
});
